import { FormEvent, useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"

import { useCreateContractorUserMutation } from "store/api/contractorsApi"
import { UITypes } from "types"
import { ContractorUserRole } from "types/ContractorUserEnum"
import styles from "assets/scss/index.module.scss"
import { ReactComponent as IconAddUser } from "assets/svg/modal-icon-add-user.svg"

import getErrorMessage from "utils/getErrorMessage"
import { createContractorUserSchema, onFormValidate, validationInitialState } from "utils/onValidate"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"

import ContractorRoleSelect from "./ContractorRoleSelect"
import ContractorUserFormFields from "./ContractorUserFormFields"

const inputValuesInitialState: UITypes.ContractorUser = {
  fullName: "",
  email: "",
  phone: "",
  userRole: ContractorUserRole.ADMIN,
}

interface Props {
  onCancel: () => void
  onSuccess: () => void
}

const AddContractorUserModal = ({ onCancel, onSuccess: onSuccess }: Props) => {
  const [createContractorUser, { isLoading }] = useCreateContractorUserMutation()
  const [inputValues, setInputValues] = useState(inputValuesInitialState)
  const [error, setError] = useState(validationInitialState)

  const onInputChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = event.currentTarget

    setInputValues(prevState => {
      return { ...prevState, [name]: type === "checkbox" ? checked : value }
    })
  }

  const onPhoneChange = (value: string) => {
    setInputValues(prevState => {
      return { ...prevState, phone: value }
    })
  }

  const onRoleChange = (value: ContractorUserRole) => {
    setInputValues(prevState => {
      return { ...prevState, userRole: value }
    })
  }

  const prepareData = () => {
    const data = {
      full_name: inputValues.fullName,
      email: inputValues.email,
      phone: inputValues.phone || "",
      user_type: inputValues.userRole!,
    }

    return data
  }

  const sendRequest = async () => {
    try {
      const res = await createContractorUser(prepareData())
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      } else {
        toast.success(
          "The new user was successfully created. Please follow the instructions in the email sent to the provided email address.",
        )
        setInputValues(inputValuesInitialState)
        onSuccess()
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error as Error)
      toast.error(errorMessage)
    }
  }

  const onSubmit = (event: FormEvent<HTMLElement>) => {
    event.preventDefault()

    if (onFormValidate(inputValues, createContractorUserSchema, setError)) {
      sendRequest()
    }
  }

  const onCancelClick = () => {
    onCancel()
  }

  const isFormValid = inputValues.fullName.trim() !== "" && inputValues.email.trim() !== ""

  return (
    <>
      {createPortal(
        <ModalLayout
          width="450px"
          confirmButtonText="Save"
          confirmButtonType="submit"
          onConfirm={onSubmit}
          onCancel={onCancelClick}
          isFetching={isLoading}
          isDisabled={!isFormValid}
        >
          <ModalHeader title="Add user">
            <IconAddUser />
          </ModalHeader>
          <div className={styles.modalContent}>
            <ContractorUserFormFields
              fullName={inputValues.fullName}
              email={inputValues.email}
              phone={inputValues.phone || ""}
              onInputChange={onInputChange}
              onPhoneChange={onPhoneChange}
              error={error}
            />
            <div>
              <ContractorRoleSelect selectedRole={inputValues.userRole} onChange={onRoleChange} />
              {error.field === "userRole" && <div className={styles.errorMessage}>{error.message}</div>}
            </div>
          </div>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}

export default AddContractorUserModal
